import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Lock, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "realtalk_admin_token";

function Stat({ label, value, tint }) {
    return (
        <div
            className="rounded-2xl border border-rt-border bg-rt-card p-5"
            data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}
        >
            <div className="text-[11px] uppercase tracking-[0.2em] text-rt-dim mb-2">
                {label}
            </div>
            <div
                className={`font-serif text-3xl sm:text-4xl tracking-tight ${
                    tint || "text-rt-text"
                }`}
            >
                {value}
            </div>
        </div>
    );
}

export default function Admin() {
    const [token, setToken] = useState(
        () => localStorage.getItem(TOKEN_KEY) || "",
    );
    const [pending, setPending] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = async (t) => {
        setLoading(true);
        setError(null);
        try {
            const r = await axios.get(`${API}/admin/analytics`, {
                headers: { "X-Admin-Token": t },
            });
            setData(r.data);
        } catch (e) {
            if (e?.response?.status === 401) {
                setError("Invalid admin token.");
                setData(null);
                localStorage.removeItem(TOKEN_KEY);
                setToken("");
            } else {
                setError("Couldn't load analytics.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) fetchData(token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const submit = (e) => {
        e.preventDefault();
        if (!pending.trim()) return;
        localStorage.setItem(TOKEN_KEY, pending.trim());
        setToken(pending.trim());
    };

    return (
        <div className="min-h-screen bg-rt-base text-rt-text">
            <Helmet>
                <title>Admin · RealTalk AI</title>
                <meta name="robots" content="noindex,nofollow" />
            </Helmet>

            <header className="sticky top-0 z-20 bg-rt-base/70 backdrop-blur-xl border-b border-white/5">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm text-rt-muted hover:text-rt-text transition"
                        data-testid="admin-home-link"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="font-serif text-base tracking-tight">
                            RealTalk · Admin
                        </span>
                    </Link>
                    {token && (
                        <button
                            onClick={() => {
                                localStorage.removeItem(TOKEN_KEY);
                                setToken("");
                                setData(null);
                            }}
                            className="text-xs text-rt-dim hover:text-rt-text transition"
                            data-testid="admin-logout"
                        >
                            Sign out
                        </button>
                    )}
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
                {!token && (
                    <form
                        onSubmit={submit}
                        className="max-w-md mx-auto text-center py-16"
                    >
                        <Lock className="w-8 h-8 text-[#B392F0] mx-auto mb-4" />
                        <h1 className="font-serif text-3xl tracking-tight mb-3">
                            Admin access
                        </h1>
                        <p className="text-rt-muted text-sm mb-8">
                            Paste the admin token to view live analytics.
                        </p>
                        <input
                            type="password"
                            autoFocus
                            value={pending}
                            onChange={(e) => setPending(e.target.value)}
                            placeholder="Admin token"
                            className="w-full bg-rt-surface border border-rt-border rounded-full px-5 py-3 text-center outline-none focus:ring-1 focus:ring-[#B392F0]/50 text-rt-text placeholder:text-rt-dim"
                            data-testid="admin-token-input"
                        />
                        <button
                            type="submit"
                            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#B392F0] hover:bg-[#C8B1F5] text-black font-medium text-sm px-6 py-2.5 transition-all hover:-translate-y-[1px]"
                            data-testid="admin-token-submit"
                        >
                            View dashboard
                        </button>
                        {error && (
                            <div
                                className="mt-4 text-sm text-red-400"
                                data-testid="admin-error"
                            >
                                {error}
                            </div>
                        )}
                    </form>
                )}

                {token && (
                    <div>
                        <div className="flex items-center justify-between mb-10">
                            <h1 className="font-serif text-3xl sm:text-4xl tracking-tight">
                                Dashboard
                            </h1>
                            {loading && (
                                <Loader2 className="w-4 h-4 animate-spin text-rt-muted" />
                            )}
                        </div>

                        {data && (
                            <>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
                                    <Stat label="Users" value={data.users} />
                                    <Stat
                                        label="Conversations"
                                        value={data.conversations}
                                    />
                                    <Stat
                                        label="Messages"
                                        value={data.messages}
                                    />
                                    <Stat
                                        label="Revenue"
                                        value={`$${data.revenue_usd_paid.toFixed(2)}`}
                                        tint="text-[#B392F0]"
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                                    <Stat
                                        label="Single unlocks"
                                        value={data.single_unlocks_paid}
                                    />
                                    <Stat
                                        label="Monthly passes"
                                        value={data.monthly_passes_paid}
                                    />
                                    <Stat
                                        label="AI messages unlocked"
                                        value={`${data.unlocked_messages} / ${data.ai_messages}`}
                                    />
                                </div>

                                <div className="rounded-2xl border border-rt-border bg-rt-card p-5 mb-10">
                                    <div className="text-[11px] uppercase tracking-[0.2em] text-rt-dim mb-4">
                                        Referral loop
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-8">
                                        <div>
                                            <div className="font-serif text-2xl">
                                                {data.referral_visits}
                                            </div>
                                            <div className="text-xs text-rt-muted mt-1">
                                                total visits from shares
                                            </div>
                                        </div>
                                        <div>
                                            <div className="font-serif text-2xl">
                                                {data.referral_conversions}
                                            </div>
                                            <div className="text-xs text-rt-muted mt-1">
                                                converted (sent a message)
                                            </div>
                                        </div>
                                        <div>
                                            <div className="font-serif text-2xl text-[#B392F0]">
                                                {data.referral_visits
                                                    ? `${Math.round(
                                                          (data.referral_conversions /
                                                              data.referral_visits) *
                                                              100,
                                                      )}%`
                                                    : "—"}
                                            </div>
                                            <div className="text-xs text-rt-muted mt-1">
                                                conversion rate
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.2em] text-rt-dim mb-4">
                                        Top shares
                                    </div>
                                    <div className="space-y-2">
                                        {data.top_shares.length === 0 && (
                                            <div className="text-sm text-rt-muted">
                                                No share traffic yet.
                                            </div>
                                        )}
                                        {data.top_shares.map((s) => (
                                            <div
                                                key={s.token}
                                                className="rounded-2xl border border-rt-border bg-rt-card p-4 flex items-start gap-4"
                                                data-testid={`top-share-${s.token}`}
                                            >
                                                <div className="font-serif text-xl text-[#B392F0] shrink-0 w-10 text-center">
                                                    {s.visits}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm text-rt-text line-clamp-2">
                                                        {s.headline ||
                                                            "(no headline)"}
                                                    </div>
                                                    <Link
                                                        to={`/s/${s.token}`}
                                                        className="inline-block mt-1 text-[11px] text-rt-dim hover:text-rt-text transition"
                                                    >
                                                        /s/{s.token} →
                                                    </Link>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}
